import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AuthGuard } from '@/components/auth/auth-guard';
import { PermissionGate } from '@/components/auth/permission-gate';
import { AppLayout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CreateVacancyDialog } from '@/features/recruitment/components/create-vacancy-dialog';
import { GlobalVacancySettingsDialog } from '@/features/recruitment/components/global-vacancy-settings-dialog';
import { VacancyPriorityBadge } from '@/features/recruitment/components/vacancy-priority-badge';
import { VacancyStatusBadge } from '@/features/recruitment/components/vacancy-status-badge';
import { useDebouncedValue } from '@/hooks/use-debounce';
import {
  useVacancies,
  useDeleteVacancy,
  useDuplicateVacancy,
  useArchiveVacancy,
  useRestoreVacancy,
} from '@/hooks/use-recruitment';
import { useDepartments } from '@/hooks/use-organizations';
import { canViewVacancies, usePermission } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import type {
  VacancyListItem,
  VacancyPriority,
  VacancyQueryParams,
  VacancyStatus,
} from '@/types/recruitment';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Archive, Copy, Edit2, MoreHorizontal, Plus, RotateCcw, Search, Settings, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

const STATUS_FILTER_OPTIONS: { value: VacancyStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'ARCHIVED', label: 'Archived' },
];

type VacancySortField = NonNullable<VacancyQueryParams['sortBy']>;
type VacancySortOrder = NonNullable<VacancyQueryParams['sortOrder']>;

// HW-590: the Vacancies list filters live in the URL query (source of truth, so
// the view is shareable/bookmarkable) and are mirrored to localStorage per user
// so the selection also survives a fresh navigation after re-login. Defaults
// (ALL / createdAt desc / empty search) are kept OUT of the URL so a pristine
// view stays on a clean `/recruitment/vacancies`.
type VacanciesSearch = {
  status?: VacancyStatus;
  priority?: VacancyPriority;
  departmentId?: string;
  q?: string;
  sortBy?: VacancySortField;
  sortOrder?: VacancySortOrder;
};

const VALID_STATUSES = new Set<string>(STATUS_FILTER_OPTIONS.map((o) => o.value));
const VALID_PRIORITIES = new Set<string>(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
const VALID_SORT_FIELDS = new Set<string>(['title', 'status', 'priority', 'createdAt']);
const VALID_SORT_ORDERS = new Set<string>(['asc', 'desc']);

const DEFAULT_SORT_BY: VacancySortField = 'createdAt';
const DEFAULT_SORT_ORDER: VacancySortOrder = 'desc';

const FILTERS_STORAGE_PREFIX = 'recruitment.vacancies.filters:';

// Accepts an untrusted bag (URL query or a localStorage payload) and keeps only
// values that map to a real filter. Shared by `validateSearch` and the
// localStorage hydration so a stale/garbage entry can never resurrect an
// invalid filter.
function sanitizeVacanciesSearch(raw: Record<string, unknown>): VacanciesSearch {
  const out: VacanciesSearch = {};
  if (typeof raw.status === 'string' && VALID_STATUSES.has(raw.status)) {
    out.status = raw.status as VacancyStatus;
  }
  if (typeof raw.priority === 'string' && VALID_PRIORITIES.has(raw.priority)) {
    out.priority = raw.priority as VacancyPriority;
  }
  if (typeof raw.departmentId === 'string' && raw.departmentId.length > 0) {
    out.departmentId = raw.departmentId;
  }
  if (typeof raw.q === 'string' && raw.q.length > 0) out.q = raw.q;
  if (typeof raw.sortBy === 'string' && VALID_SORT_FIELDS.has(raw.sortBy)) {
    out.sortBy = raw.sortBy as VacancySortField;
  }
  if (typeof raw.sortOrder === 'string' && VALID_SORT_ORDERS.has(raw.sortOrder)) {
    out.sortOrder = raw.sortOrder as VacancySortOrder;
  }
  return out;
}

// Drops defaults so the URL (and the stored payload) only carry an active
// selection. `createdAt`/`desc` and the `ALL`/empty sentinels never get written.
function normalizeVacanciesSearch(s: VacanciesSearch): VacanciesSearch {
  const out: VacanciesSearch = {};
  if (s.status) out.status = s.status;
  if (s.priority) out.priority = s.priority;
  if (s.departmentId) out.departmentId = s.departmentId;
  if (s.q) out.q = s.q;
  if (s.sortBy && s.sortBy !== DEFAULT_SORT_BY) out.sortBy = s.sortBy;
  if (s.sortOrder && s.sortOrder !== DEFAULT_SORT_ORDER) out.sortOrder = s.sortOrder;
  return out;
}

function readStoredFilters(userId: string | undefined): VacanciesSearch {
  if (!userId || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(`${FILTERS_STORAGE_PREFIX}${userId}`);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return sanitizeVacanciesSearch(parsed as Record<string, unknown>);
  } catch {
    return {};
  }
}

function writeStoredFilters(userId: string | undefined, search: VacanciesSearch) {
  if (!userId || typeof window === 'undefined') return;
  const key = `${FILTERS_STORAGE_PREFIX}${userId}`;
  try {
    if (Object.keys(search).length === 0) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(search));
  } catch {
    // localStorage may be unavailable (private mode / quota); persistence is
    // best-effort — the URL still holds the live selection.
  }
}

export const Route = createFileRoute('/recruitment/vacancies')({
  beforeLoad: () => {
    // Hot path: when the store is already initialized this stays sync, so
    // tab-switching inside /recruitment/* doesn't pay a microtask of latency.
    // Cold path: on a direct URL hit before `/auth/me` resolved we await it
    // so the gate doesn't slip past while the store is still hydrating from
    // localStorage with a stale user.
    const checkGate = () => {
      const { isAuthenticated, user } = useAuthStore.getState();
      if (!isAuthenticated) throw redirect({ to: '/login' });
      if (!canViewVacancies(user)) throw redirect({ to: '/people' });
    };
    if (useAuthStore.getState().isInitialized) {
      checkGate();
      return;
    }
    return useAuthStore.getState().initialize().then(checkGate);
  },
  validateSearch: (search: Record<string, unknown>): VacanciesSearch =>
    sanitizeVacanciesSearch(search),
  component: VacanciesPage,
});

function VacanciesPage() {
  const navigate = useNavigate();
  const canCreate = usePermission('recruitment.vacancies.create');
  const canEdit = usePermission('recruitment.vacancies.edit');
  const canDuplicate = usePermission('recruitment.vacancies.duplicate');
  const canArchive = usePermission('recruitment.vacancies.archive');
  const canDelete = usePermission('recruitment.vacancies.delete');
  const showActions = canEdit || canDuplicate || canArchive || canDelete;

  const urlSearch = Route.useSearch();
  const userId = useAuthStore((s) => s.user?.id);

  // URL query is the source of truth; localStorage mirrors it per user (HW-590).
  const status: VacancyStatus | 'ALL' = urlSearch.status ?? 'ALL';
  const priority: VacancyPriority | 'ALL' = urlSearch.priority ?? 'ALL';
  const departmentId = urlSearch.departmentId ?? 'ALL';
  const sortBy = urlSearch.sortBy ?? DEFAULT_SORT_BY;
  const sortOrder = urlSearch.sortOrder ?? DEFAULT_SORT_ORDER;

  // The search box stays local for snappy typing; the value is pushed to the URL
  // debounced so each keystroke doesn't spam the history.
  const [search, setSearch] = useState(urlSearch.q ?? '');
  const [page, setPage] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VacancyListItem | null>(null);
  const limit = 20;

  const debouncedSearch = useDebouncedValue(search, 300);

  const applySearch = (patch: Partial<VacanciesSearch>) => {
    setPage(1);
    void navigate({
      to: '/recruitment/vacancies',
      // `prev` is typed as the router-wide search union; it's this route's
      // schema at runtime (guaranteed by validateSearch), so narrow it before
      // merging the patch.
      search: (prev) => normalizeVacanciesSearch({ ...(prev as VacanciesSearch), ...patch }),
      replace: true,
    });
  };

  // One-shot hydration: when the URL carries no filters (e.g. straight after
  // re-login) fall back to the per-user localStorage snapshot. Order is
  // URL → localStorage → default, per the ticket. Gated on a state flag (not a
  // ref) so the mirror effect below never observes the pre-hydration empty URL
  // and wipes the stored snapshot.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || !userId) return;
    if (Object.keys(urlSearch).length === 0) {
      const stored = readStoredFilters(userId);
      if (Object.keys(stored).length > 0) {
        setSearch(stored.q ?? '');
        void navigate({ to: '/recruitment/vacancies', search: stored, replace: true });
      }
    }
    setHydrated(true);
  }, [userId]);

  // Mirror the live URL selection into localStorage for next time.
  const storedKey = JSON.stringify(urlSearch);
  useEffect(() => {
    if (hydrated && userId) writeStoredFilters(userId, urlSearch);
  }, [hydrated, userId, storedKey]);

  // Keep the input in sync when the URL `q` changes from outside the box
  // (hydration / back-forward). During active typing the debounce hasn't written
  // yet, so this is a no-op and never clobbers the caret.
  useEffect(() => {
    setSearch(urlSearch.q ?? '');
  }, [urlSearch.q]);

  // Push the debounced search text into the URL.
  useEffect(() => {
    if (debouncedSearch === (urlSearch.q ?? '')) return;
    applySearch({ q: debouncedSearch || undefined });
  }, [debouncedSearch]);

  const params: VacancyQueryParams = {
    page,
    limit,
    search: debouncedSearch || undefined,
    status: status === 'ALL' ? undefined : status,
    priority: priority === 'ALL' ? undefined : priority,
    departmentId: departmentId === 'ALL' ? undefined : departmentId,
    sortBy,
    sortOrder,
  };

  const { data, isLoading } = useVacancies(params);
  const { data: departments = [] } = useDepartments();
  const deleteMutation = useDeleteVacancy();
  const duplicateMutation = useDuplicateVacancy();
  const archiveMutation = useArchiveVacancy();
  const restoreMutation = useRestoreVacancy();

  const vacancies = data?.data ?? [];
  const meta = data?.meta;

  const pendingId =
    (duplicateMutation.isPending && duplicateMutation.variables) ||
    (archiveMutation.isPending && archiveMutation.variables) ||
    (restoreMutation.isPending && restoreMutation.variables) ||
    (deleteMutation.isPending && deleteMutation.variables) ||
    null;

  const openVacancy = (id: string) =>
    navigate({ to: '/recruitment/vacancies/$id', params: { id } });

  // Mirror Candidates: count only the real facet filters (sort is excluded) so
  // the Clear-filters control only shows when there's something to reset.
  const activeFilterCount =
    (debouncedSearch ? 1 : 0) +
    (status !== 'ALL' ? 1 : 0) +
    (priority !== 'ALL' ? 1 : 0) +
    (departmentId !== 'ALL' ? 1 : 0);

  const clearFilters = () => {
    setSearch('');
    setPage(1);
    // Empty search drops the URL query; the localStorage mirror effect then sees
    // an empty selection and removes the stored snapshot (HW-590), so the next
    // mount hydrates to defaults instead of re-restoring the cleared filters.
    void navigate({ to: '/recruitment/vacancies', search: {}, replace: true });
  };

  const handleSort = (field: VacancyQueryParams['sortBy']) => {
    if (!field) return;
    const nextOrder: VacancySortOrder =
      sortBy === field ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
    applySearch({ sortBy: field, sortOrder: nextOrder });
  };

  const handleDuplicate = async (id: string) => {
    const created = await duplicateMutation.mutateAsync(id);
    openVacancy(created.id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  };

  const SortHeader = ({
    field,
    children,
    className,
  }: {
    field: VacancyQueryParams['sortBy'];
    children: React.ReactNode;
    className?: string;
  }) => (
    <th
      className={`px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none ${className ?? ''}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy === field && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );

  const colSpan = showActions ? 11 : 10;

  return (
    <AuthGuard>
      <AppLayout>
        <header className="sticky top-14 z-30 flex h-12 items-center justify-between border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <span>Recruitment</span>
            <span>/</span>
            <span className="font-medium text-foreground">Vacancies</span>
          </div>
          <PermissionGate permission="recruitment.pipeline.manage">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => setSettingsDialogOpen(true)}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Pipeline templates</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </PermissionGate>
        </header>

        <GlobalVacancySettingsDialog
          open={settingsDialogOpen}
          onOpenChange={setSettingsDialogOpen}
        />

        <div className="flex-1 space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Vacancies</h1>
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground">
                {meta ? `${meta.total} total` : ''}
              </div>
              {canCreate && (
                <Button variant="accent" onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Vacancy
                </Button>
              )}
            </div>
          </div>

          <CreateVacancyDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            onSuccess={(id) => openVacancy(id)}
          />

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search vacancies..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={status}
              onValueChange={(v) =>
                applySearch({ status: v === 'ALL' ? undefined : (v as VacancyStatus) })
              }
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={priority}
              onValueChange={(v) =>
                applySearch({ priority: v === 'ALL' ? undefined : (v as VacancyPriority) })
              }
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Priorities</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={departmentId}
              onValueChange={(v) =>
                applySearch({ departmentId: v === 'ALL' ? undefined : v })
              }
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Departments</SelectItem>
                {departments.map((d: { id: string; name: string }) => (
                  <SelectItem
                    key={d.id}
                    value={d.id}
                    className="[&>span:last-child]:truncate"
                    title={d.name}
                  >
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {activeFilterCount > 0 && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[1100px]">
              <thead className="border-b bg-muted/50">
                <tr>
                  <SortHeader field="title">Title</SortHeader>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Department</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Location</th>
                  <SortHeader field="status">Status</SortHeader>
                  <SortHeader field="priority">Priority</SortHeader>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Active</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Openings</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Recruiter</th>
                  <SortHeader field="createdAt">Created</SortHeader>
                  {showActions && <th className="w-[56px] px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : vacancies.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-8 text-center text-muted-foreground">
                      No vacancies found
                    </td>
                  </tr>
                ) : (
                  vacancies.map((v) => {
                    const isArchived = v.status === 'ARCHIVED';
                    const busy = pendingId === v.id;
                    return (
                      <tr
                        key={v.id}
                        className="border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => openVacancy(v.id)}
                      >
                        <td className="px-4 py-3">
                          <div
                            className="max-w-xs truncate font-medium text-foreground"
                            title={v.title}
                          >
                            {v.title}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                          {v.publicId}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          <div className="max-w-[200px] truncate" title={v.department?.name}>
                            {v.department?.name ?? '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          <div className="max-w-[200px] truncate" title={v.location ?? undefined}>
                            {v.location ?? '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <VacancyStatusBadge status={v.status} />
                        </td>
                        <td className="px-4 py-3">
                          <VacancyPriorityBadge priority={v.priority} />
                        </td>
                        <td className="px-4 py-3 text-sm tabular-nums">
                          {v.activeCandidatesCount}
                        </td>
                        <td className="px-4 py-3 text-sm tabular-nums">{v.openings}</td>
                        <td className="px-4 py-3">
                          <div className="flex -space-x-2">
                            {v.recruiters?.slice(0, 3).map((r) => (
                              <div
                                key={r.id}
                                className="h-7 w-7 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs font-medium"
                                title={`${r.firstName} ${r.lastName}`}
                              >
                                {r.avatarUrl ? (
                                  <img
                                    src={r.avatarUrl}
                                    alt=""
                                    className="h-full w-full rounded-full object-cover"
                                  />
                                ) : (
                                  `${r.firstName[0]}${r.lastName[0]}`
                                )}
                              </div>
                            ))}
                            {(v.recruiters?.length ?? 0) > 3 && (
                              <div className="h-7 w-7 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs">
                                +{v.recruiters!.length - 3}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(v.createdAt).toLocaleDateString()}
                        </td>
                        {showActions && (
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground"
                                  disabled={busy}
                                  aria-label="Vacancy actions"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                {canEdit && (
                                  <DropdownMenuItem onSelect={() => openVacancy(v.id)}>
                                    <Edit2 className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                {canDuplicate && (
                                  <DropdownMenuItem onSelect={() => handleDuplicate(v.id)}>
                                    <Copy className="mr-2 h-4 w-4" />
                                    Duplicate
                                  </DropdownMenuItem>
                                )}
                                {canArchive &&
                                  (isArchived ? (
                                    <DropdownMenuItem onSelect={() => restoreMutation.mutate(v.id)}>
                                      <RotateCcw className="mr-2 h-4 w-4" />
                                      Restore
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onSelect={() => archiveMutation.mutate(v.id)}>
                                      <Archive className="mr-2 h-4 w-4" />
                                      Archive
                                    </DropdownMenuItem>
                                  ))}
                                {canDelete && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onSelect={() => setDeleteTarget(v)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {vacancies.length} of {meta.total} vacancies
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {meta.page} of {meta.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= meta.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        <AlertDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete vacancy?</AlertDialogTitle>
              {/* wrap-anywhere, not break-words: both break inside an unbroken token, but only
                  `anywhere` also lowers the element's min-content width, so it holds even where
                  the container is sized by its content. Used consistently for that reason. */}
              <AlertDialogDescription className="wrap-anywhere">
                Permanently delete <b>{deleteTarget?.title}</b>? This removes the vacancy and its
                candidate links. Cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  void confirmDelete();
                }}
                disabled={deleteMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AppLayout>
    </AuthGuard>
  );
}
